/**
 * Automatisches Speichern der Grundlagen (Beschreibung, Umfang, Merkmale,
 * Nutzung).
 *
 * Betreiber 01.11.2026: „wenn man was eingegeben hat, muss man manuell
 * speichern — baue um, dass automatisch wie bei den anderen Modulen auch
 * gespeichert wird". Loest Entscheid E-L3 („kein Autosave, Weiter
 * speichert") ab. Dessen Begruendung — jedes Speichern loeste die
 * Fassungsrueckfrage als Dialog aus — traegt nicht mehr: nach einem
 * automatischen Speichern erscheint sie als Leiste, nicht mitten im Tippen.
 *
 * Reine Regeln: WANN gespeichert wird und WAS die Zustandszeile sagt. Den
 * Schreibweg haelt `PlanungModul` — derselbe wie bisher der Knopf
 * („Grundlagen speichern"), samt festgeschriebener Pruefung.
 */

/** Ruhe nach der letzten Eingabe, bevor gespeichert wird. */
export const AUTOSPEICHERN_RUHE_MS = 1500;
/** Abstand der Wiederholungen nach einem Fehlschlag. */
export const AUTOSPEICHERN_WIEDERHOLUNG_MS = 10_000;
/** Nach so vielen Fehlschlaegen ruht es, bis wieder etwas eingegeben oder
 *  „Erneut versuchen" geklickt wird — sonst liefe bei totem Netz endlos
 *  ein Versuch nach dem anderen. */
export const AUTOSPEICHERN_MAX_VERSUCHE = 3;
/** Ruhe nach dem letzten Speichern, bevor die geltenden Fassungen geprueft
 *  werden — nicht nach jedem Tastendruckpaket zwei Abfragen. */
export const FASSUNGSPRUEFUNG_RUHE_MS = 5000;

export interface AutoSpeicherLage {
    /** Der Entwurf weicht vom gespeicherten Satz ab. */
    schmutzig: boolean;
    isReadOnly: boolean;
    abgeschlossen: boolean;
    /** Nichts schreiben: die Phase wird gerade gelesen, konnte nicht gelesen
     *  werden, oder der Entwurf gehoert nicht zum angezeigten Projekt. */
    gesperrt: boolean;
    /** Eine Aktion laeuft unter der Aktionssperre (`mitLauf`) — sie schreibt
     *  den Entwurf selbst oder kommt zuerst; danach wird neu geplant. */
    laeuft: boolean;
    /** Ein automatisches Speichern ist schon unterwegs. */
    autoLaeuft: boolean;
    /** Fehlgeschlagene Versuche seit der letzten Eingabe. */
    fehlversuche: number;
    /** Die Maske der Grundlagen ist zu sehen. Ist sie weg (andere Station,
     *  anderer Reiter, Ablage offen), wird ohne Wartezeit gespeichert — der
     *  naechste Bereich arbeitet mit dem gespeicherten Stand. */
    maskeSichtbar: boolean;
}

/** Ob und nach welcher Wartezeit gespeichert wird. */
export const autoSpeicherPlan = (lage: AutoSpeicherLage): { speichern: boolean; wartezeit: number } => {
    const ruhe = { speichern: false, wartezeit: 0 };
    if (!lage.schmutzig || lage.isReadOnly || lage.abgeschlossen || lage.gesperrt) return ruhe;
    if (lage.laeuft || lage.autoLaeuft) return ruhe;
    const versuche = Math.max(0, Math.floor(lage.fehlversuche || 0));
    if (versuche >= AUTOSPEICHERN_MAX_VERSUCHE) return ruhe;
    if (versuche > 0) return { speichern: true, wartezeit: AUTOSPEICHERN_WIEDERHOLUNG_MS };
    return { speichern: true, wartezeit: lage.maskeSichtbar ? AUTOSPEICHERN_RUHE_MS : 0 };
};

export type SpeicherTon = 'ruhig' | 'wartend' | 'warnung';

export interface Speicherstand {
    text: string;
    ton: SpeicherTon;
    /** „Erneut versuchen" anbieten. */
    erneut: boolean;
}

/**
 * Die Zustandszeile neben den Grundlagen. Rangfolge der Dringlichkeit: ein
 * gescheitertes Speichern schlaegt alles andere — sonst glaubt der Anwender,
 * seine Angaben laegen in der Akte. „Gespeichert" nur, wenn es einen
 * gespeicherten Kopfsatz gibt; sonst waere es eine leere Behauptung.
 */
export const speicherstand = (lage: {
    schmutzig: boolean;
    abgeschlossen: boolean;
    isReadOnly: boolean;
    hatPhase: boolean;
    autoLaeuft: boolean;
    fehlversuche: number;
    /** Uhrzeit des letzten Speicherns in dieser Sitzung („14:32"). */
    zuletzt: string | null;
    /** Die Phase wird gelesen oder konnte nicht gelesen werden — dann wird
     *  nicht automatisch gespeichert (`AutoSpeicherLage.gesperrt`). */
    gesperrt?: boolean;
}): Speicherstand => {
    const stand = (text: string, ton: SpeicherTon, erneut = false): Speicherstand => ({ text, ton, erneut });
    if (lage.abgeschlossen) {
        return lage.schmutzig
            ? stand('Nicht gespeichert — die Planungsphase ist abgeschlossen', 'warnung')
            : stand('Abgeschlossen', 'ruhig');
    }
    // Kippt die Nur-Ansicht mit offenem Entwurf (Projektsperre an jemand
    // anderen), ist er nicht gespeichert — das sagen, nicht „Gespeichert".
    if (lage.isReadOnly) {
        if (lage.schmutzig) return stand('Nicht gespeichert — nur Ansicht', 'warnung');
        return stand(lage.hatPhase ? 'Gespeichert' : 'Noch nichts erfasst', 'ruhig');
    }
    if (lage.schmutzig && lage.gesperrt && !lage.autoLaeuft) return stand('Nicht gespeichert', 'warnung');
    if (lage.autoLaeuft) return stand('Wird gespeichert …', 'wartend');
    if (lage.schmutzig && lage.fehlversuche > 0) {
        return lage.fehlversuche >= AUTOSPEICHERN_MAX_VERSUCHE
            ? stand('Nicht gespeichert', 'warnung', true)
            : stand('Nicht gespeichert — neuer Versuch folgt', 'warnung', true);
    }
    if (lage.schmutzig) return stand('Wird gespeichert …', 'wartend');
    if (lage.zuletzt) return stand(`Gespeichert ${lage.zuletzt}`, 'ruhig');
    return stand(lage.hatPhase ? 'Gespeichert' : 'Noch nichts erfasst', 'ruhig');
};

/** Farbe der Zustandszeile je Ton — eine Stelle fuer Reiter und Leitfaden. */
export const SPEICHER_TON_KLASSE: Record<SpeicherTon, string> = {
    ruhig: 'text-slate-400',
    wartend: 'text-slate-500',
    warnung: 'text-rose-700',
};

/** „14:32" in Ortszeit. */
export const uhrzeitKurz = (d: Date): string =>
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
